import { BRANCH_HIDDEN, STEM_ELEMENT, type Branch, type FourPillars, type Stem } from '@/utils/baziCalc';
import { buildWuxingDetail, type WuxingDetailItem } from '@/utils/calcTools/bazi/wuxingEngine';
import { getRizhuInfo } from '@/utils/calcTools/bazi/rizhuEngine';
import { judgeGeju, type GejuResult } from '@/utils/calcTools/bazi/gejuEngine';
import { calcXiyong } from '@/utils/calcTools/bazi/xiyongEngine';
import { calcDayun, type DayunResult } from '@/utils/calcTools/bazi/dayunEngine';
import { calcLiunianRange, getCurrentLiunian, type LiunianItem } from '@/utils/calcTools/bazi/liunianEngine';
import { calcShensha, type ShenshaItem } from '@/utils/calcTools/bazi/shenshaEngine';
import { calcYueling, type YuelingResult } from '@/utils/calcTools/bazi/yuelingEngine';
import { calcXiaoyun, type XiaoyunItem } from '@/utils/calcTools/bazi/xiaoyunEngine';
import {
  calcShishenDistribution,
  calcShishenStructure,
  ELEMENT_COLORS,
  getShishen,
  type ShishenCountItem,
  type ShishenStructure,
} from '@/utils/calcTools/bazi/shishenEngine';
import { NAYIN_MAP } from '@/utils/calcTools/chineseZodiac/wuxingCalc';
import type { DaYun, LiuNian, Pillar } from '@/pages/paipan/types';

interface GanZhiPalace {
  ganZhi: string;
  naYin: string;
  tianGan: string;
  diZhi: string;
  tianGanColor?: string;
  diZhiColor?: string;
}

interface MingGongData {
  tianGan: string;
  diZhi: string;
  naYin: string;
  tianGanColor?: string;
  diZhiColor?: string;
}

const STEMS10 = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
const BRANCHES12 = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
const CHANG_SHENG_NAMES = ['长生','沐浴','冠带','临官','帝旺','衰','病','死','墓','绝','胎','养'];
const CHANG_SHENG_MAP: Record<string, string[]> = {
  甲: ['亥','子','丑','寅','卯','辰','巳','午','未','申','酉','戌'],
  乙: ['午','巳','辰','卯','寅','丑','子','亥','戌','酉','申','未'],
  丙: ['寅','卯','辰','巳','午','未','申','酉','戌','亥','子','丑'],
  丁: ['酉','申','未','午','巳','辰','卯','寅','丑','子','亥','戌'],
  戊: ['寅','卯','辰','巳','午','未','申','酉','戌','亥','子','丑'],
  己: ['酉','申','未','午','巳','辰','卯','寅','丑','子','亥','戌'],
  庚: ['巳','午','未','申','酉','戌','亥','子','丑','寅','卯','辰'],
  辛: ['子','亥','戌','酉','申','未','午','巳','辰','卯','寅','丑'],
  壬: ['申','酉','戌','亥','子','丑','寅','卯','辰','巳','午','未'],
  癸: ['卯','寅','丑','子','亥','戌','酉','申','未','午','巳','辰'],
};
const XUNCHANG_TABLE: Record<string, { xunShou: string; kongWang: string }> = {
  甲子: { xunShou: '甲子旬', kongWang: '戌亥' },
  甲戌: { xunShou: '甲戌旬', kongWang: '申酉' },
  甲申: { xunShou: '甲申旬', kongWang: '午未' },
  甲午: { xunShou: '甲午旬', kongWang: '辰巳' },
  甲辰: { xunShou: '甲辰旬', kongWang: '寅卯' },
  甲寅: { xunShou: '甲寅旬', kongWang: '子丑' },
};

function stemFromIdx(idx: number): string { return STEMS10[((idx % 10) + 10) % 10]; }
function branchFromIdx(idx: number): string { return BRANCHES12[((idx % 12) + 12) % 12]; }
function getStemColor(stem: string): string {
  return STEM_ELEMENT[stem as keyof typeof STEM_ELEMENT] ? ELEMENT_COLORS[STEM_ELEMENT[stem as keyof typeof STEM_ELEMENT]] : '#E8E0E8';
}
function getBranchColor(branch: string): string {
  const mainStem = BRANCH_HIDDEN[branch as keyof typeof BRANCH_HIDDEN]?.[0]?.stem;
  return mainStem ? getStemColor(mainStem) : '#E8C39E';
}
function getNaYin(stem: string, branch: string): string {
  return NAYIN_MAP[`${stem}${branch}`] || '';
}
function makeGanZhiPalace(stemIdx: number, branchIdx: number): GanZhiPalace {
  const tianGan = stemFromIdx(stemIdx);
  const diZhi = branchFromIdx(branchIdx);
  const naYin = getNaYin(tianGan, diZhi);
  return {
    ganZhi: `${tianGan}${diZhi}${naYin ? `（${naYin}）` : ''}`,
    naYin,
    tianGan,
    diZhi,
    tianGanColor: getStemColor(tianGan),
    diZhiColor: getBranchColor(diZhi),
  };
}
function calcMingGongPalace(fp: FourPillars): GanZhiPalace {
  const monthBranchIdx = BRANCHES12.indexOf(fp.month.branch);
  const hourBranchIdx = BRANCHES12.indexOf(fp.hour.branch);
  const monthSeq = ((monthBranchIdx - 2 + 12) % 12) + 1;
  const hourSeq = hourBranchIdx + 1;
  const mingGongSeq = ((18 - monthSeq - hourSeq - 1) % 12 + 12) % 12 + 1;
  const mingGongBranchIdx = mingGongSeq - 1;
  const yearStemIdx = STEMS10.indexOf(fp.year.stem);
  const monthStemStarts = [2, 4, 6, 8, 0, 2, 4, 6, 8, 0];
  const startStemIdx = monthStemStarts[yearStemIdx] ?? 2;
  const mingGongMonthOffset = ((mingGongBranchIdx - 2 + 12) % 12);
  return makeGanZhiPalace(startStemIdx + mingGongMonthOffset, mingGongBranchIdx);
}
function calcTaiYuanPillar(fp: FourPillars): GanZhiPalace {
  return makeGanZhiPalace(STEMS10.indexOf(fp.month.stem) + 1, BRANCHES12.indexOf(fp.month.branch) + 3);
}
function calcTaiZhuPillar(fp: FourPillars): GanZhiPalace {
  return makeGanZhiPalace(STEMS10.indexOf(fp.year.stem) - 1, BRANCHES12.indexOf(fp.year.branch) - 1);
}
function getChangSheng(stem: string, branch: string): string {
  const stages = CHANG_SHENG_MAP[stem];
  const idx = stages?.indexOf(branch) ?? -1;
  return idx >= 0 ? CHANG_SHENG_NAMES[idx] : '';
}
function getXunShouAndKongWang(stem: string, branch: string): { xunShou: string; kongWang: string } {
  const si = STEMS10.indexOf(stem);
  const bi = BRANCHES12.indexOf(branch);
  if (si < 0 || bi < 0) return { xunShou: '', kongWang: '' };
  const xunStartBi = ((bi - si) % 12 + 12) % 12;
  const xunKey = `甲${BRANCHES12[xunStartBi]}`;
  return XUNCHANG_TABLE[xunKey] || { xunShou: `甲${BRANCHES12[xunStartBi]}旬`, kongWang: '' };
}
function buildQiYunInfo(startAge: number, isForward: boolean): string {
  return `出生后${startAge}年起运，${isForward ? '顺排' : '逆排'}大运`;
}
function buildJiaoYunInfo(startAge: number, startMonths: number, birthYear: number, birthMonth: number, startDateTime?: string): string {
  let jiaoYear = birthYear + startAge;
  let jiaoMonth = birthMonth + startMonths;
  if (jiaoMonth > 12) { jiaoYear += 1; jiaoMonth -= 12; }
  let base = `${jiaoYear}年${jiaoMonth}月（${startAge}岁${startMonths > 0 ? startMonths + '个月' : ''}）交大运`;
  if (startDateTime) base += `\n交运时刻：${startDateTime}`;
  return base;
}
function toExtraPillar(p: GanZhiPalace, label: string): Pillar {
  return {
    label,
    tianGan: p.tianGan,
    diZhi: p.diZhi,
    cangGan: [],
    xingYun: '',
    ziZuo: '',
    kongWang: '',
    naYin: p.naYin,
    shenSha: '',
    tianGanColor: p.tianGanColor,
    diZhiColor: p.diZhiColor,
  };
}
function buildProfessionalPillars(fp: FourPillars, shenshaItems: ShenshaItem[], gender?: string): Pillar[] {
  const pillarLabels = ['年柱', '月柱', '日柱', '时柱'];
  const pillarShenshaMap: Record<string, string[]> = { 年柱: [], 月柱: [], 日柱: [], 时柱: [] };
  shenshaItems.forEach(item => item.pillars.forEach(pillar => { if (pillarShenshaMap[pillar]) pillarShenshaMap[pillar].push(item.name); }));
  const main = [fp.year, fp.month, fp.day, fp.hour].map((p, idx) => {
    const hidden = BRANCH_HIDDEN[p.branch as keyof typeof BRANCH_HIDDEN] || [];
    const isDay = idx === 2;
    return {
      label: pillarLabels[idx],
      mainStar: isDay ? (normalizeBaziGender(gender) === 'female' ? '元女' : '元男') : getShishen(fp.day.stem, p.stem),
      tianGan: p.stem,
      diZhi: p.branch,
      cangGan: hidden.map(h => `${h.stem}${getShishen(fp.day.stem, h.stem)}`),
      cangGanColors: hidden.map(h => getStemColor(h.stem)),
      xingYun: getChangSheng(fp.day.stem, p.branch),
      ziZuo: getChangSheng(p.stem, p.branch),
      kongWang: getXunShouAndKongWang(p.stem, p.branch).kongWang,
      naYin: getNaYin(p.stem, p.branch),
      shenSha: (pillarShenshaMap[pillarLabels[idx]] || []).join(' '),
      tianGanColor: getStemColor(p.stem),
      diZhiColor: getBranchColor(p.branch),
      isHighlight: isDay,
    } as Pillar;
  });
  return [...main, toExtraPillar(calcTaiZhuPillar(fp), '胎柱'), toExtraPillar(calcTaiYuanPillar(fp), '胎元')];
}

export interface SharedXiyongApproach {
  label: string;
  desc?: string;
  xi: Array<{ element: string; color?: string }>;
  ji: Array<{ element: string; color?: string }>;
  summary?: string;
}

/** 喜用神数据：本地引擎结果或服务端 pro.yongShen 映射（以服务端为准，二者公共字段兼容） */
export interface SharedXiyongData {
  xi: Array<{ element: string; color?: string }>;
  ji: Array<{ element: string; color?: string }>;
  bodyStrength?: 'strong' | 'weak' | 'neutral';
  wangShuaiLabel?: string;
  advice?: string;
  approaches?: SharedXiyongApproach[];
}

export interface SharedBaziDetailData {
  fourPillars: FourPillars;
  wuxingDetail: WuxingDetailItem[];
  rizhu: ReturnType<typeof getRizhuInfo>;
  geju: GejuResult;
  xiyong: SharedXiyongData;
  dayun: DayunResult;
  currentLiunian: LiunianItem;
  liunianRange: LiunianItem[];
  shensha: ShenshaItem[];
  tableShensha: ShenshaItem[];
  yueling: YuelingResult | null;
  xiaoyun: XiaoyunItem[];
  shishenDistribution: ShishenCountItem[];
  shishenStructure: ShishenStructure;
  professionalPillars: Pillar[];
  professionalDaYun: DaYun[];
  professionalLiuNian: LiuNian[];
  professionalMingGong: MingGongData;
  qiYunInfo: string;
  jiaoYunInfo: string;
  startAge: number;
  startMonths: number;
  isForward: boolean;
  preciseJiaoYear: number;
  preciseJiaoMonth: number;
  currentYear: number;
}

export function normalizeBaziGender(gender?: string): 'male' | 'female' {
  return gender?.includes('女') || gender === 'female' ? 'female' : 'male';
}

export function parseBirthDateParts(birthTime: string): { by: number; bm: number; bd: number; bh: number; bmn: number } {
  const [datePart, timePart = '0:0'] = birthTime.split(' ');
  const [by, bm, bd] = datePart.split('-').map(Number);
  const [bh = 0, bmn = 0] = timePart.split(':').map(Number);
  return { by, bm, bd, bh, bmn };
}

/** 服务端 tyme4j 四柱格式 -> 本地 FourPillars 格式 */
export type ServerFourPillars = {
  year?: { stem?: string; branch?: string; label?: string };
  month?: { stem?: string; branch?: string; label?: string };
  day?: { stem?: string; branch?: string; label?: string };
  hour?: { stem?: string; branch?: string; label?: string };
};

export function toFourPillars(fp: ServerFourPillars | undefined): FourPillars | null {
  if (!fp?.year?.stem || !fp?.month?.stem || !fp?.day?.stem || !fp?.hour?.stem) return null;
  return {
    year: { stem: fp.year.stem as Stem, branch: fp.year.branch as Branch, label: '年柱' },
    month: { stem: fp.month.stem as Stem, branch: fp.month.branch as Branch, label: '月柱' },
    day: { stem: fp.day.stem as Stem, branch: fp.day.branch as Branch, label: '日柱' },
    hour: { stem: fp.hour.stem as Stem, branch: fp.hour.branch as Branch, label: '时柱' },
  };
}

export function buildProfessionalPillarsExport(fp: FourPillars, shenshaItems: ShenshaItem[], gender?: string): Pillar[] {
  return buildProfessionalPillars(fp, shenshaItems, gender);
}

export function calcMingGongPalaceExport(fp: FourPillars): MingGongData {
  const mingGongPalace = calcMingGongPalace(fp);
  return {
    tianGan: mingGongPalace.tianGan,
    diZhi: mingGongPalace.diZhi,
    naYin: mingGongPalace.naYin,
    tianGanColor: mingGongPalace.tianGanColor,
    diZhiColor: mingGongPalace.diZhiColor,
  };
}

export function buildQiYunInfoExport(startAge: number, isForward: boolean): string {
  return buildQiYunInfo(startAge, isForward);
}

export function buildJiaoYunInfoExport(
  startAge: number, startMonths: number, birthYear: number, birthMonth: number, startDateTime?: string,
): string {
  return buildJiaoYunInfo(startAge, startMonths, birthYear, birthMonth, startDateTime);
}

export function buildSharedBaziDetailData(params: {
  birthTime: string;
  birthPlace?: string;
  birthLongitude?: number;
  birthTimezone?: number;
  gender?: string;
  currentYear: number;
  lang?: string;
  /** 服务端 tyme4j 四柱（仅此来源，缺失即抛错，不回退本地） */
  serverFourPillars?: ServerFourPillars;
}): SharedBaziDetailData {
  const { birthTime, birthPlace, birthLongitude, birthTimezone, gender, currentYear, lang = 'zh-CN', serverFourPillars } = params;
  const fourPillars = toFourPillars(serverFourPillars);
  if (!fourPillars) {
    throw new Error('服务端排盘数据缺失');
  }
  const { by, bm, bd, bh, bmn } = parseBirthDateParts(birthTime);
  const genderKey = normalizeBaziGender(gender);
  const geju = judgeGeju(fourPillars);
  const wuxingDetail = buildWuxingDetail(fourPillars);
  const xiyong = calcXiyong(fourPillars);
  const dayun = calcDayun(fourPillars, by, bm, bd, genderKey, currentYear, bh || 0, bmn || 0);
  const shenshaGender = genderKey === 'female' ? 2 : 1;
  const shensha = calcShensha(fourPillars, lang, shenshaGender);
  const tableShensha = calcShensha(fourPillars, 'zh-CN', shenshaGender);
  const professionalPillars = buildProfessionalPillars(fourPillars, tableShensha, gender);
  const professionalDaYun: DaYun[] = dayun.items.map(item => ({
    startYear: item.startYear,
    endYear: item.endYear,
    ageRange: `${item.startAge}~${item.endAge}岁`,
    ageRange_en: `${item.startAge}~${item.endAge} yrs`,
    tianGan: item.stem,
    diZhi: item.branch,
    tianGanShen: item.stemShishen,
    diZhiShen: item.branchMainShishen,
    isActive: item.isActive,
    tianGanColor: item.stemColor,
    diZhiColor: item.branchColor,
  }));
  const liunianRange = calcLiunianRange(fourPillars, by, bm, bd, genderKey, currentYear, { past: 1, future: 9 });
  const professionalLiuNian: LiuNian[] = liunianRange.map(item => ({
    year: item.year,
    tianGan: item.stem,
    diZhi: item.branch,
    tianGanShen: item.stemShishen,
    diZhiShen: item.branchShishen,
    liuYue: '',
    liuYue_en: '',
    tianGanColor: item.stemColor,
    diZhiColor: item.branchColor,
    isHighlight: item.isCurrent,
  }));
  const mingGongPalace = calcMingGongPalace(fourPillars);
  const jiaoMonthRaw = bm + dayun.startMonths;
  const preciseJiaoYear = by + dayun.startAge + (jiaoMonthRaw > 12 ? 1 : 0);
  const preciseJiaoMonth = jiaoMonthRaw > 12 ? jiaoMonthRaw - 12 : jiaoMonthRaw;

  return {
    fourPillars,
    wuxingDetail,
    rizhu: getRizhuInfo(fourPillars.day.stem),
    geju,
    xiyong,
    dayun,
    currentLiunian: getCurrentLiunian(fourPillars, by, bm, bd, genderKey, currentYear),
    liunianRange,
    shensha,
    tableShensha,
    yueling: calcYueling(fourPillars, birthTime),
    xiaoyun: calcXiaoyun(fourPillars, by, dayun.startAge, dayun.isForward, currentYear, 20, lang),
    shishenDistribution: calcShishenDistribution(fourPillars),
    shishenStructure: calcShishenStructure(fourPillars),
    professionalPillars,
    professionalDaYun,
    professionalLiuNian,
    professionalMingGong: {
      tianGan: mingGongPalace.tianGan,
      diZhi: mingGongPalace.diZhi,
      naYin: mingGongPalace.naYin,
      tianGanColor: mingGongPalace.tianGanColor,
      diZhiColor: mingGongPalace.diZhiColor,
    },
    qiYunInfo: buildQiYunInfo(dayun.startAge, dayun.isForward),
    jiaoYunInfo: buildJiaoYunInfo(dayun.startAge, dayun.startMonths, by, bm, dayun.startDateTime),
    startAge: dayun.startAge,
    startMonths: dayun.startMonths,
    isForward: dayun.isForward,
    preciseJiaoYear,
    preciseJiaoMonth,
    currentYear,
  };
}
